import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { clinicAPI, adminAPI, handleApiError } from '../../../services/api';
import VerifyClinicModal from './VerifyClinicModal';
import RejectClinicModal from './RejectClinicModal';
import './ClinicManager.css';

const CLINICS_PER_PAGE = 15;

const ClinicManager = () => {
  const { t } = useTranslation();

  const [clinics, setClinics] = useState([]);
  const [allClinics, setAllClinics] = useState([]);
  const [totalClinics, setTotalClinics] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [apiError, setApiError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');

  const [selectedClinic, setSelectedClinic] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);

  const [showVerifyModal, setShowVerifyModal] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [actionError, setActionError] = useState(null);

  // Filters
  const [statusFilter, setStatusFilter] = useState('all');
  const [planFilter, setPlanFilter] = useState('all');
  const [countryFilter, setCountryFilter] = useState('');
  const [statusDropdownOpen, setStatusDropdownOpen] = useState(false);
  const [planDropdownOpen, setPlanDropdownOpen] = useState(false);
  const [countryDropdownOpen, setCountryDropdownOpen] = useState(false);
  const [countrySearch, setCountrySearch] = useState('');
  const [availableCountries, setAvailableCountries] = useState([]);

  const isLoadingRef = useRef(false);

  const loadClinics = useCallback(async () => {
    if (isLoadingRef.current) return;
    isLoadingRef.current = true;
    setIsLoading(true);
    setApiError(null);
    try {
      const response = await clinicAPI.getClinics(searchQuery || '');

      let clinicsData = [];
      if (response.data) {
        if (response.data.data) {
          if (response.data.data.clinics && Array.isArray(response.data.data.clinics)) {
            clinicsData = response.data.data.clinics;
          } else if (Array.isArray(response.data.data)) {
            clinicsData = response.data.data;
          } else if (typeof response.data.data === 'object') {
            clinicsData = [response.data.data];
          }
        } else if (Array.isArray(response.data)) {
          clinicsData = response.data;
        } else if (response.data.clinics && Array.isArray(response.data.clinics)) {
          clinicsData = response.data.clinics;
        }
      }

      const clinicsWithStatus = clinicsData.map(clinic => {
        let status = clinic.status || clinic.verification_status;
        if (!status) {
          if (clinic.is_verified === true || clinic.verified === true) {
            status = 'verified';
          } else {
            status = 'unverified';
          }
        }
        return { ...clinic, status };
      });

      setTotalClinics(clinicsWithStatus);

      // Extract unique countries from all clinics for the dropdown
      const countryMap = new Map();
      clinicsWithStatus.forEach(c => {
        const countryObj = c.country;
        const countryId = (typeof countryObj === 'object' && countryObj !== null)
          ? (countryObj.id || countryObj.code || c.country_id)
          : (c.country_id || c.country);
        const countryName = (typeof countryObj === 'object' && countryObj !== null)
          ? (countryObj.name_en || countryObj.name_ar || countryObj.name || c.country_name || String(countryId))
          : (c.country_name || c.country || String(countryId));
        if (countryId !== undefined && countryId !== null) {
          if (!countryMap.has(String(countryId))) {
            countryMap.set(String(countryId), { id: String(countryId), name: countryName });
          }
        }
      });
      setAvailableCountries(Array.from(countryMap.values()).sort((a, b) => a.name.localeCompare(b.name)));

      let filtered = clinicsWithStatus;

      // Country filter
      if (countryFilter) {
        filtered = filtered.filter(c => {
          const countryObj = c.country;
          const cid = (typeof countryObj === 'object' && countryObj !== null)
            ? (countryObj.id || countryObj.code || c.country_id)
            : (c.country_id || c.country);
          return String(cid) === String(countryFilter);
        });
      }

      // Status filter
      if (statusFilter === 'verified') {
        filtered = filtered.filter(c => c.status === 'verified');
      } else if (statusFilter === 'unverified') {
        filtered = filtered.filter(c => c.status === 'unverified');
      } else if (statusFilter === 'rejected') {
        filtered = filtered.filter(c => c.status === 'rejected');
      }

      // Plan filter
      if (planFilter === 'free') {
        filtered = filtered.filter(c => {
          if (c.status !== 'verified') return false;
          const subscriptionPlan = c.clinic_subscription?.plan || c.clinic_subscription_plan;
          return subscriptionPlan !== 'paid';
        });
      } else if (planFilter === 'paid') {
        filtered = filtered.filter(c => {
          if (c.status !== 'verified') return false;
          const subscriptionPlan = c.clinic_subscription?.plan || c.clinic_subscription_plan;
          return subscriptionPlan === 'paid';
        });
      } else if (planFilter === 'upgrade-requests') {
        filtered = filtered.filter(c => {
          if (c.status !== 'verified') return false;
          const hasUpgradeRequest =
            c.clinic_subscription?.upgrade_requested === true ||
            c.clinic_subscription?.has_upgrade_request === true ||
            c.upgrade_requested === true ||
            c.has_upgrade_request === true;
          if (!hasUpgradeRequest) return false;
          const subscriptionPlan = c.clinic_subscription?.plan || c.clinic_subscription_plan;
          return subscriptionPlan !== 'paid';
        });
      }

      setAllClinics(filtered);
      setCurrentPage(1);

      const startIndex = 0;
      const endIndex = CLINICS_PER_PAGE;
      const paginatedClinics = filtered.slice(startIndex, endIndex);
      setClinics(paginatedClinics);
    } catch (error) {
      handleApiError(error);
      setApiError(error?.response?.data?.message || error.message || 'Failed to load clinics');
      setClinics([]);
      setAllClinics([]);
    } finally {
      setIsLoading(false);
      isLoadingRef.current = false;
    }
  }, [statusFilter, planFilter, countryFilter, searchQuery]);

  useEffect(() => {
    loadClinics();
  }, [loadClinics]);

  useEffect(() => {
    const startIndex = (currentPage - 1) * CLINICS_PER_PAGE;
    const endIndex = startIndex + CLINICS_PER_PAGE;
    const paginatedClinics = allClinics.slice(startIndex, endIndex);
    setClinics(paginatedClinics);
  }, [currentPage, allClinics]);

  const handleVerifyClinic = async (clinicId) => {
    try {
      await adminAPI.verifyClinic(clinicId, 'verified');
      await loadClinics();
      setShowVerifyModal(false);
      if (selectedClinic?.id === clinicId) {
        setSelectedClinic(prev => prev ? { ...prev, status: 'verified' } : null);
      }
    } catch (error) {
      setActionError(error?.response?.data?.message || error.message || 'Failed to verify clinic');
    }
  };

  const handleRejectClinic = async (clinicId, reason) => {
    try {
      await adminAPI.verifyClinic(clinicId, 'rejected', reason || null);
      await loadClinics();
      setShowRejectModal(false);
      if (selectedClinic?.id === clinicId) {
        setSelectedClinic(prev => prev ? { ...prev, status: 'rejected' } : null);
      }
    } catch (error) {
      setActionError(error?.response?.data?.message || error.message || 'Failed to reject clinic');
    }
  };

  const handleUpgradeSubscription = async (clinicId) => {
    try {
      await adminAPI.upgradeClinicSubscription(clinicId);
      await loadClinics();
      if (selectedClinic?.id === clinicId) {
        setSelectedClinic(prev => {
          if (!prev) return null;
          const updated = { ...prev };
          if (updated.clinic_subscription) {
            updated.clinic_subscription = { ...updated.clinic_subscription, plan: 'paid' };
          }
          updated.clinic_subscription_plan = 'paid';
          return updated;
        });
      }
    } catch (error) {
      setActionError(error?.response?.data?.message || error.message || 'Failed to upgrade subscription');
    }
  };

  const handleDowngradeSubscription = async (clinicId) => {
    try {
      await adminAPI.downgradeClinicSubscription(clinicId);
      await loadClinics();
      if (selectedClinic?.id === clinicId) {
        setSelectedClinic(prev => {
          if (!prev) return null;
          const updated = { ...prev };
          if (updated.clinic_subscription) {
            updated.clinic_subscription = { ...updated.clinic_subscription, plan: 'free' };
          }
          updated.clinic_subscription_plan = 'free';
          return updated;
        });
      }
    } catch (error) {
      setActionError(error?.response?.data?.message || error.message || 'Failed to downgrade subscription');
    }
  };

  const handleSelectClinic = (clinic) => {
    setSelectedClinic(clinic);
  };

  const getClinicName = (clinic) => {
    if (!clinic) return '—';
    return clinic.name_en || clinic.name_ar || t('adminDashboard.card.notAvailable');
  };

  const getClinicCity = (clinic) => {
    if (!clinic) return '—';
    if (typeof clinic.city === 'object') {
      return clinic.city?.name_en || clinic.city?.name_ar || clinic.city?.name || '';
    }
    return clinic.city_name || clinic.city || '';
  };

  const getSubscriptionPlan = (clinic) => {
    if (!clinic) return null;
    return clinic.clinic_subscription?.plan || clinic.clinic_subscription_plan || null;
  };

  const hasUpgradeRequest = (clinic) => {
    if (!clinic) return false;
    return (
      clinic.clinic_subscription?.upgrade_requested === true ||
      clinic.clinic_subscription?.has_upgrade_request === true ||
      clinic.upgrade_requested === true ||
      clinic.has_upgrade_request === true
    );
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'verified': return t('adminDashboard.card.status.verified');
      case 'rejected': return t('adminDashboard.card.status.rejected');
      default: return t('adminDashboard.card.status.unverified');
    }
  };

  const getClinicCountryId = (clinic) => {
    const countryObj = clinic.country;
    return (typeof countryObj === 'object' && countryObj !== null)
      ? (countryObj.id || countryObj.code || clinic.country_id)
      : (clinic.country_id || clinic.country);
  };

  const matchesPlanFilter = (clinic, plan) => {
    if (plan === 'all') return true;
    if (clinic.status !== 'verified') return false;
    const subscriptionPlan = clinic.clinic_subscription?.plan || clinic.clinic_subscription_plan;
    if (plan === 'free') return subscriptionPlan !== 'paid';
    if (plan === 'paid') return subscriptionPlan === 'paid';
    if (plan === 'upgrade-requests') {
      const hasUpg = hasUpgradeRequest(clinic);
      return hasUpg && subscriptionPlan !== 'paid';
    }
    return true;
  };

  const matchesStatusFilter = (clinic, status) => {
    if (status === 'all') return true;
    return clinic.status === status;
  };

  const matchesCountryFilter = (clinic, countryId) => {
    if (!countryId) return true;
    return String(getClinicCountryId(clinic)) === String(countryId);
  };

  // Preview count: how many results if we applied this filter value while keeping other current filters
  const getPreviewCount = (filterType, value) => {
    return totalClinics.filter(c => {
      const countryMatch = filterType === 'country'
        ? matchesCountryFilter(c, value)
        : matchesCountryFilter(c, countryFilter);
      const statusMatch = filterType === 'status'
        ? matchesStatusFilter(c, value)
        : matchesStatusFilter(c, statusFilter);
      const planMatch = filterType === 'plan'
        ? matchesPlanFilter(c, value)
        : matchesPlanFilter(c, planFilter);
      return countryMatch && statusMatch && planMatch;
    }).length;
  };

  const totalPages = Math.ceil(allClinics.length / CLINICS_PER_PAGE) || 1;

  const isSelectedUnverified = selectedClinic?.status === 'unverified';
  const isSelectedVerified = selectedClinic?.status === 'verified';
  const isSelectedRejected = selectedClinic?.status === 'rejected';
  const isSelectedPaid = getSubscriptionPlan(selectedClinic) === 'paid';
  const selectedHasUpgrade = hasUpgradeRequest(selectedClinic);

  const displayClinic = selectedClinic || {};

  return (
    <div className="clinic-manager">
      <div className="clinic-manager-layout">
        {/* Left Panel - Clinic List */}
        <div className="clinic-manager-panel clinic-manager-list">
          <div className="clinic-manager-panel-header">
            <h3 className="clinic-manager-panel-title">
              {t('adminDashboard.menu.clinics', 'Clinics')}
            </h3>
            <span className="clinic-manager-count">{allClinics.length}</span>
          </div>
          <div className="clinic-manager-filters">
            <div className="clinic-manager-search" style={{ borderBottom: 'none', padding: 0, background: 'transparent' }}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="11" cy="11" r="8"></circle>
                <path d="m21 21-4.35-4.35"></path>
              </svg>
              <input
                type="text"
                placeholder={t('adminDashboard.searchPlaceholder', 'Search clinics by name or registration number...')}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
              {searchQuery && (
                <button
                  className="clinic-manager-search-clear"
                  onClick={() => setSearchQuery('')}
                  title={t('common.clear', 'Clear')}
                >
                  ✕
                </button>
              )}
            </div>
            {/* Country Filter */}
            <div className="clinic-manager-filter" style={{ position: 'relative' }}>
              <button
                className="clinic-manager-filter-toggle"
                onClick={() => {
                  setCountryDropdownOpen(o => !o);
                  setStatusDropdownOpen(false);
                  setPlanDropdownOpen(false);
                }}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10"></circle>
                  <line x1="2" y1="12" x2="22" y2="12"></line>
                  <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"></path>
                </svg>
                <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', textAlign: 'left' }}>
                  {countryFilter
                    ? availableCountries.find(c => String(c.id) === String(countryFilter))?.name || t('adminDashboard.userManagement.country', 'Country')
                    : t('adminDashboard.userManagement.allCountries', 'All Countries')}
                </span>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ transform: countryDropdownOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }}>
                  <polyline points="6 9 12 15 18 9"></polyline>
                </svg>
              </button>
              {countryDropdownOpen && (
                <div className="clinic-manager-filter-dropdown">
                  <div className="clinic-manager-filter-search">
                    <input
                      type="text"
                      placeholder={t('adminDashboard.userManagement.searchCountry', 'Search country...')}
                      value={countrySearch}
                      onChange={(e) => setCountrySearch(e.target.value)}
                      autoFocus
                    />
                  </div>
                  <div className="clinic-manager-filter-list">
                    <button
                      className={`clinic-manager-filter-item ${!countryFilter ? 'active' : ''}`}
                      onClick={() => { setCountryFilter(''); setCountryDropdownOpen(false); setCountrySearch(''); }}
                    >
                      <span>{t('adminDashboard.userManagement.allCountries', 'All Countries')}</span>
                      <span className="clinic-manager-filter-count">{getPreviewCount('country', '')}</span>
                    </button>
                    {availableCountries
                      .filter(c => !countrySearch || c.name.toLowerCase().includes(countrySearch.toLowerCase()))
                      .map(country => (
                        <button
                          key={country.id}
                          className={`clinic-manager-filter-item ${String(countryFilter) === String(country.id) ? 'active' : ''}`}
                          onClick={() => { setCountryFilter(String(country.id)); setCountryDropdownOpen(false); setCountrySearch(''); }}
                        >
                          <span>{country.name}</span>
                          <span className="clinic-manager-filter-count">{getPreviewCount('country', country.id)}</span>
                        </button>
                      ))}
                  </div>
                  {countryFilter && (
                    <button
                      className="clinic-manager-filter-clear-btn"
                      onClick={() => { setCountryFilter(''); setCountryDropdownOpen(false); setCountrySearch(''); }}
                    >
                      {t('adminDashboard.userManagement.clearFilter', 'Clear filter')}
                    </button>
                  )}
                </div>
              )}
            </div>
            {/* Status Filter */}
            <div className="clinic-manager-filter-row">
              <div className="clinic-manager-filter">
                <button
                  className="clinic-manager-filter-toggle"
                  onClick={() => { setStatusDropdownOpen(o => !o); setPlanDropdownOpen(false); setCountryDropdownOpen(false); }}
                >
                  <span>
                    {statusFilter === 'all' ? t('adminDashboard.filters.status.all', 'All Status')
                      : statusFilter === 'verified' ? t('adminDashboard.filters.status.verified', 'Verified')
                      : statusFilter === 'unverified' ? t('adminDashboard.filters.status.unverified', 'Unverified')
                      : t('adminDashboard.filters.status.rejected', 'Rejected')}
                  </span>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ transform: statusDropdownOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }}>
                    <polyline points="6 9 12 15 18 9"></polyline>
                  </svg>
                </button>
                {statusDropdownOpen && (
                  <div className="clinic-manager-filter-dropdown">
                    {[
                      { key: 'all', label: t('adminDashboard.filters.status.all', 'All Status') },
                      { key: 'verified', label: t('adminDashboard.filters.status.verified', 'Verified') },
                      { key: 'unverified', label: t('adminDashboard.filters.status.unverified', 'Unverified') },
                      { key: 'rejected', label: t('adminDashboard.filters.status.rejected', 'Rejected') },
                    ].map(opt => (
                      <button
                        key={opt.key}
                        className={`clinic-manager-filter-item ${statusFilter === opt.key ? 'active' : ''}`}
                        onClick={() => { setStatusFilter(opt.key); setStatusDropdownOpen(false); }}
                      >
                        <span>{opt.label}</span>
                        <span className="clinic-manager-filter-count">{getPreviewCount('status', opt.key)}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
              {/* Plan Filter */}
              <div className="clinic-manager-filter">
                <button
                  className="clinic-manager-filter-toggle"
                  onClick={() => { setPlanDropdownOpen(o => !o); setStatusDropdownOpen(false); setCountryDropdownOpen(false); }}
                >
                  <span>
                    {planFilter === 'all' ? t('adminDashboard.filters.plan.all', 'All Plans')
                      : planFilter === 'free' ? t('adminDashboard.filters.plan.free', 'Free')
                      : planFilter === 'paid' ? t('adminDashboard.filters.plan.paid', 'Paid')
                      : t('adminDashboard.filters.plan.upgradeRequests', 'Upgrade Requests')}
                  </span>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ transform: planDropdownOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }}>
                    <polyline points="6 9 12 15 18 9"></polyline>
                  </svg>
                </button>
                {planDropdownOpen && (
                  <div className="clinic-manager-filter-dropdown">
                    {[
                      { key: 'all', label: t('adminDashboard.filters.plan.all', 'All Plans') },
                      { key: 'free', label: t('adminDashboard.filters.plan.free', 'Free') },
                      { key: 'paid', label: t('adminDashboard.filters.plan.paid', 'Paid') },
                      { key: 'upgrade-requests', label: t('adminDashboard.filters.plan.upgradeRequests', 'Upgrade Requests') },
                    ].map(opt => (
                      <button
                        key={opt.key}
                        className={`clinic-manager-filter-item ${planFilter === opt.key ? 'active' : ''}`}
                        onClick={() => { setPlanFilter(opt.key); setPlanDropdownOpen(false); }}
                      >
                        <span>{opt.label}</span>
                        <span className="clinic-manager-filter-count">{getPreviewCount('plan', opt.key)}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
          <div className="clinic-manager-list-content">
            {isLoading ? (
              <div className="clinic-manager-loading">{t('adminDashboard.loading', 'Loading clinics...')}</div>
            ) : apiError ? (
              <div className="clinic-manager-empty clinic-manager-error">
                <p>{apiError}</p>
                <button className="clinic-manager-retry-btn" onClick={() => loadClinics()}>
                  {t('common.retry', 'Retry')}
                </button>
              </div>
            ) : clinics.length === 0 ? (
              <div className="clinic-manager-empty">
                <p>{t('adminDashboard.empty.noMatch', 'No clinics match your filters.')}</p>
              </div>
            ) : (
              clinics.map(clinic => (
                <div
                  key={clinic.id}
                  className={`clinic-manager-list-item ${selectedClinic?.id === clinic.id ? 'selected' : ''}`}
                  onClick={() => handleSelectClinic(clinic)}
                >
                  <div className="clinic-manager-list-item-info">
                    <h4>{getClinicName(clinic)}</h4>
                    <p>{clinic.registration_number || getClinicCity(clinic) || '—'}</p>
                  </div>
                  <div className="clinic-manager-list-item-meta">
                    <span className={`clinic-manager-status-badge ${clinic.status || 'unverified'}`}>
                      {getStatusLabel(clinic.status)}
                    </span>
                    {getSubscriptionPlan(clinic) && (
                      <span className="clinic-manager-list-item-plan">
                        {getSubscriptionPlan(clinic)}
                      </span>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>

          {totalPages > 1 && (
            <div className="clinic-manager-pagination">
              <button
                disabled={currentPage <= 1}
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polyline points="15 18 9 12 15 6"></polyline>
                </svg>
                {t('adminDashboard.pagination.previous', 'Previous')}
              </button>
              <span>
                {t('adminDashboard.pagination.page', { current: currentPage, total: totalPages })}
              </span>
              <button
                disabled={currentPage >= totalPages}
                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
              >
                {t('adminDashboard.pagination.next', 'Next')}
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polyline points="9 18 15 12 9 6"></polyline>
                </svg>
              </button>
            </div>
          )}
        </div>

        {/* Right Panel - Clinic Details */}
        <div className="clinic-manager-panel clinic-manager-details">
          {!selectedClinic ? (
            <div className="clinic-manager-details-empty">
              <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                <line x1="9" y1="3" x2="9" y2="21"></line>
                <line x1="3" y1="9" x2="21" y2="9"></line>
              </svg>
              <p>{t('adminDashboard.empty.selectClinic', 'Select a clinic to view details and manage its status.')}</p>
            </div>
          ) : (
            <>
              <div className="clinic-manager-details-header">
                <div className="clinic-manager-details-header-main">
                  {displayClinic.logo_url || displayClinic.logo ? (
                    <img
                      src={displayClinic.logo_url || displayClinic.logo}
                      alt={getClinicName(displayClinic)}
                      className="clinic-manager-details-logo"
                    />
                  ) : (
                    <div className="clinic-manager-details-logo-placeholder">
                      {getClinicName(displayClinic).charAt(0).toUpperCase()}
                    </div>
                  )}
                  <div style={{ minWidth: 0 }}>
                    <h2>{getClinicName(displayClinic)}</h2>
                    <p className="clinic-manager-details-subtitle">
                      <span className={`clinic-manager-status-badge ${displayClinic.status || 'unverified'}`}>
                        {getStatusLabel(displayClinic.status)}
                      </span>
                      {displayClinic.registration_number && (
                        <span>Reg: {displayClinic.registration_number}</span>
                      )}
                      {getSubscriptionPlan(displayClinic) && (
                        <span>Plan: {getSubscriptionPlan(displayClinic)}</span>
                      )}
                    </p>
                  </div>
                </div>
                <div className="clinic-manager-details-actions">
                  {isSelectedUnverified && (
                    <>
                      <button
                        className="clinic-manager-action-btn verify"
                        onClick={() => setShowVerifyModal(true)}
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                          <polyline points="22 4 12 14.01 9 11.01"></polyline>
                        </svg>
                        {t('adminDashboard.card.actions.verify')}
                      </button>
                      <button
                        className="clinic-manager-action-btn reject"
                        onClick={() => setShowRejectModal(true)}
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <circle cx="12" cy="12" r="10"></circle>
                          <line x1="15" y1="9" x2="9" y2="15"></line>
                          <line x1="9" y1="9" x2="15" y2="15"></line>
                        </svg>
                        {t('adminDashboard.card.actions.reject')}
                      </button>
                    </>
                  )}
                  {isSelectedRejected && (
                    <button
                      className="clinic-manager-action-btn verify"
                      onClick={() => setShowVerifyModal(true)}
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                        <polyline points="22 4 12 14.01 9 11.01"></polyline>
                      </svg>
                      {t('adminDashboard.card.actions.verify')}
                    </button>
                  )}
                  {isSelectedVerified && (
                    <>
                      {!isSelectedPaid && selectedHasUpgrade ? (
                        <button
                          className="clinic-manager-action-btn upgrade"
                          onClick={() => handleUpgradeSubscription(displayClinic.id)}
                        >
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M12 2v20M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
                          </svg>
                          {t('adminDashboard.card.actions.upgradeToPaid')}
                        </button>
                      ) : isSelectedPaid ? (
                        <button
                          className="clinic-manager-action-btn downgrade"
                          onClick={() => handleDowngradeSubscription(displayClinic.id)}
                        >
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M12 22v-20M7 19H14.5a3.5 3.5 0 0 0 0-7h-5a3.5 3.5 0 0 1 0-7H18" />
                          </svg>
                          {t('adminDashboard.card.actions.downgradeToFree')}
                        </button>
                      ) : null}
                    </>
                  )}
                </div>
              </div>

              <div className="clinic-manager-details-content">
                {/* Logo */}
                {(displayClinic.logo_url || displayClinic.logo) && (
                  <div className="clinic-manager-section">
                    <h3>{t('adminDashboard.modal.sections.logo')}</h3>
                    <img
                      src={displayClinic.logo_url || displayClinic.logo}
                      alt={`${getClinicName(displayClinic)} logo`}
                      style={{ maxWidth: '120px', maxHeight: '120px', borderRadius: '8px', border: '1px solid #e2e8f0' }}
                    />
                  </div>
                )}

                {/* Basic Information */}
                <div className="clinic-manager-section">
                  <h3>{t('adminDashboard.modal.sections.basicInformation')}</h3>
                  <div className="clinic-manager-info-grid">
                    <div className="clinic-manager-info-item">
                      <label>{t('adminDashboard.modal.fields.registrationNumber')}</label>
                      <span>{displayClinic.registration_number || '—'}</span>
                    </div>
                    <div className="clinic-manager-info-item">
                      <label>{t('adminDashboard.modal.fields.establishmentDate')}</label>
                      <span>{displayClinic.establishment_date ? new Date(displayClinic.establishment_date).toLocaleDateString() : '—'}</span>
                    </div>
                    <div className="clinic-manager-info-item">
                      <label>{t('adminDashboard.modal.fields.vatNumber')}</label>
                      <span>{displayClinic.vat_number || '—'}</span>
                    </div>
                    <div className="clinic-manager-info-item">
                      <label>{t('adminDashboard.modal.fields.clinicType')}</label>
                      <span>
                        {(() => {
                          if (displayClinic.clinic_type && typeof displayClinic.clinic_type === 'object' && !Array.isArray(displayClinic.clinic_type)) {
                            return displayClinic.clinic_type.name_en || displayClinic.clinic_type.name_ar || displayClinic.clinic_type.name || '—';
                          }
                          if (displayClinic.type_name) return displayClinic.type_name;
                          if (displayClinic.type && typeof displayClinic.type === 'string') return displayClinic.type;
                          const typeId = displayClinic.type || displayClinic.clinic_type;
                          if (typeof typeId === 'number') return typeId.toString();
                          return '—';
                        })()}
                      </span>
                    </div>
                    <div className="clinic-manager-info-item">
                      <label>{t('adminDashboard.modal.fields.currency')}</label>
                      <span>{displayClinic.currency || '—'}</span>
                    </div>
                    <div className="clinic-manager-info-item">
                      <label>{t('adminDashboard.modal.fields.invoiceStartingNumber')}</label>
                      <span>{displayClinic.invoice_starting_number || displayClinic.invoice_start || '—'}</span>
                    </div>
                    <div className="clinic-manager-info-item">
                      <label>{t('adminDashboard.modal.fields.petStore')}</label>
                      <span>
                        {displayClinic.is_pet_store === true ? t('adminDashboard.modal.values.yes')
                          : displayClinic.is_pet_store === false ? t('adminDashboard.modal.values.no')
                          : '—'}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Contact Information */}
                <div className="clinic-manager-section">
                  <h3>{t('adminDashboard.modal.sections.contactInformation')}</h3>
                  <div className="clinic-manager-info-grid">
                    <div className="clinic-manager-info-item">
                      <label>{t('adminDashboard.modal.fields.email')}</label>
                      <span>{displayClinic.email || '—'}</span>
                    </div>
                    <div className="clinic-manager-info-item">
                      <label>{t('adminDashboard.modal.fields.telephone')}</label>
                      <span>{displayClinic.telephone || '—'}</span>
                    </div>
                    <div className="clinic-manager-info-item">
                      <label>{t('adminDashboard.modal.fields.mobile')}</label>
                      <span>{displayClinic.mobile || '—'}</span>
                    </div>
                    <div className="clinic-manager-info-item">
                      <label>{t('adminDashboard.modal.fields.website')}</label>
                      <span>
                        {displayClinic.website ? (
                          <a href={displayClinic.website} target="_blank" rel="noopener noreferrer">{displayClinic.website}</a>
                        ) : '—'}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Location */}
                <div className="clinic-manager-section">
                  <h3>{t('adminDashboard.modal.sections.location')}</h3>
                  <div className="clinic-manager-info-grid">
                    <div className="clinic-manager-info-item">
                      <label>{t('adminDashboard.modal.fields.country')}</label>
                      <span>
                        {typeof displayClinic.country === 'object'
                          ? (displayClinic.country?.name_en || displayClinic.country?.name_ar || displayClinic.country?.name || '—')
                          : (displayClinic.country_name || displayClinic.country || '—')}
                      </span>
                    </div>
                    <div className="clinic-manager-info-item">
                      <label>{t('adminDashboard.modal.fields.city')}</label>
                      <span>
                        {typeof displayClinic.city === 'object'
                          ? (displayClinic.city?.name_en || displayClinic.city?.name_ar || displayClinic.city?.name || '—')
                          : (displayClinic.city_name || displayClinic.city || '—')}
                      </span>
                    </div>
                    <div className="clinic-manager-info-item">
                      <label>{t('adminDashboard.modal.fields.street')}</label>
                      <span>{displayClinic.street || displayClinic.full_address || displayClinic.address || '—'}</span>
                    </div>
                    <div className="clinic-manager-info-item">
                      <label>{t('adminDashboard.modal.fields.buildingNumber')}</label>
                      <span>{displayClinic.building_number || '—'}</span>
                    </div>
                    <div className="clinic-manager-info-item">
                      <label>{t('adminDashboard.modal.fields.floorNumber')}</label>
                      <span>{displayClinic.floor_number || '—'}</span>
                    </div>
                    {(displayClinic.latitude && displayClinic.longitude) && (
                      <div className="clinic-manager-info-item">
                        <label>{t('adminDashboard.modal.fields.coordinates')}</label>
                        <span>{displayClinic.latitude}, {displayClinic.longitude}</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Description */}
                {displayClinic.description && (
                  <div className="clinic-manager-section">
                    <h3>{t('adminDashboard.modal.sections.description')}</h3>
                    <p className="clinic-manager-description">{displayClinic.description}</p>
                  </div>
                )}

                {/* Services */}
                {displayClinic.services_offered && displayClinic.services_offered.length > 0 && (
                  <div className="clinic-manager-section">
                    <h3>{t('adminDashboard.modal.sections.servicesOffered')}</h3>
                    <div className="clinic-manager-services">
                      {displayClinic.services_offered.map((service, index) => (
                        <span key={index} className="clinic-manager-service-tag">
                          {service.name || service}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Working Hours */}
                {displayClinic.working_hours && Object.keys(displayClinic.working_hours).length > 0 && (
                  <div className="clinic-manager-section">
                    <h3>{t('adminDashboard.modal.sections.workingHours')}</h3>
                    <div className="clinic-manager-working-hours">
                      {Object.entries(displayClinic.working_hours).map(([day, hours]) => (
                        hours.enabled && (
                          <div key={day} className="clinic-manager-working-hour">
                            <span className="clinic-manager-working-day">{day}</span>
                            <span className="clinic-manager-working-time">{hours.from} - {hours.to}</span>
                          </div>
                        )
                      ))}
                    </div>
                  </div>
                )}

                {/* Owner Information */}
                {(() => {
                  const owner = displayClinic.owner || displayClinic.user;
                  const ownerName = owner
                    ? (owner.full_name || `${owner.first_name || ''} ${owner.last_name || ''}`.trim() || owner.name || owner.username || '—')
                    : null;
                  if (!owner && !ownerName) return null;
                  return (
                    <div className="clinic-manager-section">
                      <h3>{t('adminDashboard.modal.sections.ownerInformation')}</h3>
                      <div className="clinic-manager-info-grid">
                        <div className="clinic-manager-info-item">
                          <label>{t('adminDashboard.modal.fields.name')}</label>
                          <span>{ownerName || '—'}</span>
                        </div>
                        {owner?.email && (
                          <div className="clinic-manager-info-item">
                            <label>{t('adminDashboard.modal.fields.email')}</label>
                            <span>{owner.email}</span>
                          </div>
                        )}
                        {owner?.username && (
                          <div className="clinic-manager-info-item">
                            <label>{t('adminDashboard.modal.fields.username')}</label>
                            <span>{owner.username}</span>
                          </div>
                        )}
                        {owner?.phone && (
                          <div className="clinic-manager-info-item">
                            <label>{t('adminDashboard.modal.fields.phone')}</label>
                            <span>{owner.phone}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })()}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Modals */}
      {showVerifyModal && selectedClinic && (
        <VerifyClinicModal
          isOpen={showVerifyModal}
          onClose={() => setShowVerifyModal(false)}
          clinic={selectedClinic}
          onConfirm={() => handleVerifyClinic(selectedClinic.id)}
        />
      )}

      {showRejectModal && selectedClinic && (
        <RejectClinicModal
          isOpen={showRejectModal}
          onClose={() => setShowRejectModal(false)}
          clinic={selectedClinic}
          onConfirm={(reason) => handleRejectClinic(selectedClinic.id, reason)}
        />
      )}

      {/* Error Toast */}
      {actionError && (
        <div className="clinic-manager-error-toast">
          <div className="clinic-manager-error-content">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10"></circle>
              <line x1="15" y1="9" x2="9" y2="15"></line>
              <line x1="9" y1="9" x2="15" y2="15"></line>
            </svg>
            <span>{actionError}</span>
            <button onClick={() => setActionError(null)}>✕</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClinicManager;
